import { type FormEvent, type ReactNode, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Brand } from "../components/Brand";
import { PasswordInput } from "../components/PasswordInput";
import { supabase } from "../lib/supabase";

const resetMessage = "If an account exists for this email, you will receive a password reset link.";

function PasswordCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="login-page">
      <section className="login-card">
        <Brand />
        <div className="login-card__intro">
          <p className="eyebrow">Just Kasz CRM</p>
          <h1>{title}</h1>
        </div>
        <div className="form-stack">{children}</div>
      </section>
      <aside className="login-page__brand-panel" aria-hidden="true">
        <div className="brand-panel__ring" />
        <p>Good food. Good people. Better follow-through.</p>
      </aside>
    </main>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current || sent || !supabase) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    // A minimum feedback interval avoids an instant unknown-account response in
    // the UI. Supabase owns endpoint responses and server-side abuse protection.
    const feedbackDelay = new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim());
      await feedbackDelay;
      if (resetError && (!resetError.status || resetError.status >= 500)) {
        setError("We could not send your request. Check your connection and try again.");
        return;
      }
      // Unknown users and throttled requests get the same acknowledgement.
      // Never expose Auth error details or perform an account lookup here.
      setSent(true);
      setEmail("");
    } catch {
      await feedbackDelay;
      setError("We could not send your request. Check your connection and try again.");
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <PasswordCard title={sent ? "Check your inbox" : "Forgot your password?"}>
      {sent ? (
        <>
          <p role="status">{resetMessage}</p>
          <p>
            Check your spam folder too. Requests are limited, so wait at least a minute before
            trying again.
          </p>
          <p>Open the email link, then select Confirm and continue to choose a new password.</p>
        </>
      ) : (
        <>
          <p>Enter your account email to request a password reset link.</p>
          <form onSubmit={(event) => void submit(event)} className="form-stack">
            <label>
              Email
              <input
                name="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                maxLength={254}
                required
              />
            </label>
            {error && (
              <p className="error-message" role="alert">
                {error}
              </p>
            )}
            {!supabase && (
              <p role="alert">
                Password reset is unavailable. Please contact your CRM administrator.
              </p>
            )}
            <button
              type="submit"
              className="button button--primary"
              disabled={submitting || !supabase}
              aria-busy={submitting}
            >
              {submitting ? "Sending request..." : "Send reset link"}
            </button>
          </form>
        </>
      )}
      <Link to="/" className="button button--secondary">
        Back to sign in
      </Link>
    </PasswordCard>
  );
}

export function ResetPasswordPage() {
  const { user, loading, updatePassword, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current || finished) return;
    setError(null);
    if (!saved && (password.length < 12 || password !== confirmation)) {
      setError(
        password.length < 12 ? "Use at least 12 characters." : "The passwords do not match.",
      );
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    let passwordSaved = saved;
    try {
      if (!passwordSaved) {
        await updatePassword(password);
        passwordSaved = true;
        setSaved(true);
        setPassword("");
        setConfirmation("");
      }
      // Supabase global sign-out revokes refresh tokens on every device.
      // If it fails, retry sign-out without submitting the new password again.
      await signOut();
      setFinished(true);
    } catch {
      setError(
        passwordSaved
          ? "Your password was updated, but sign-out could not finish. Please retry sign-out."
          : "Your password could not be updated. Use a different password of at least 12 characters, or request a new reset link if this session has expired.",
      );
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <PasswordCard title={finished ? "Password updated" : "Set a new password"}>
      {finished ? (
        <>
          <p role="status">Your password has been updated. Sign in with your new password.</p>
          <Link to="/" className="button button--primary">
            Back to sign in
          </Link>
        </>
      ) : saved || (!loading && user) ? (
        <form onSubmit={(event) => void submit(event)} className="form-stack">
          {!saved && (
            <>
              <p>
                Choose a unique password for {user?.email || "your account"}. You will need to sign
                in again after saving.
              </p>
              <div className="password-field">
                <label htmlFor="passwordresetpage-password-1">New password</label>
                <PasswordInput
                  id="passwordresetpage-password-1"
                  name="password"
                  autoComplete="new-password"
                  minLength={12}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  aria-describedby="password-help"
                  required
                />
              </div>
              <p id="password-help">
                Use at least 12 characters. You can paste a password from your password manager.
              </p>
              <div className="password-field">
                <label htmlFor="passwordresetpage-password-2">Confirm new password</label>
                <PasswordInput
                  id="passwordresetpage-password-2"
                  name="confirmation"
                  autoComplete="new-password"
                  minLength={12}
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  required
                />
              </div>
            </>
          )}
          {error && (
            <p className="error-message" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="button button--primary"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting
              ? "Saving and signing out..."
              : saved
                ? "Retry sign-out"
                : "Save new password"}
          </button>
          {!saved && <Link to="/auth/forgot-password">Request a new reset link</Link>}
        </form>
      ) : loading ? (
        <p role="status">Checking your session...</p>
      ) : (
        <>
          <p>Open and confirm the link from your reset email before choosing a new password.</p>
          <Link to="/auth/forgot-password" className="button button--primary">
            Request a new reset link
          </Link>
          <Link to="/" className="button button--secondary">
            Back to sign in
          </Link>
        </>
      )}
    </PasswordCard>
  );
}
